import { Button, DeleteIcon, PlusIcon } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { Form as AntdForm, type FormListFieldData } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { type ReactNode, useId } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import StepFieldLabel from './StepFieldLabel'

type StepRowListProps = {
  addLabel: ReactNode
  /** What one row is, for its Remove button, e.g. "Team member"; else `label`. */
  itemLabel?: string
  /** Shown above every row's input, which it names for screen readers. */
  label: string
  /** The form field holding the rows. */
  name: string | Array<string>
  /** The value a new row starts with. */
  newRow?: unknown
  /** The input for one row; give it `id` so the label names it. */
  renderInput: (row: FormListFieldData, index: number, id: string) => ReactNode
  testId: string
}

/**
 * A list of rows the user adds and removes, as designed: each row a labelled
 * input with Remove beside it (below it when narrow), and Add under the list.
 */
const StepRowList = ({
  addLabel,
  itemLabel,
  label,
  name,
  newRow,
  renderInput,
  testId
}: StepRowListProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const idPrefix = useId()

  return (
    <List data-testid={testId}>
      <AntdForm.List name={name}>
        {(rows, { add, remove }) => (
          <>
            {rows.length > 0 && (
              <Rows>
                {rows.map((row, index) => {
                  const id = `${idPrefix}-${row.key}`
                  return (
                    <Row key={row.key}>
                      <Field>
                        <label htmlFor={id}>
                          <StepFieldLabel label={label} />
                        </label>
                        {renderInput(row, index, id)}
                      </Field>
                      <Button
                        aria-label={t(
                          incidentReportsConstants.CASE_V2_REMOVE_ROW,
                          { label: itemLabel ?? label, number: index + 1 }
                        )}
                        data-testid={`${testId}-remove-${index}`}
                        icon={<DeleteIcon size={16} />}
                        onClick={() => remove(row.name)}
                        variant="transparent-critical"
                      >
                        {t(incidentReportsConstants.CASE_V2_REMOVE)}
                      </Button>
                    </Row>
                  )
                })}
              </Rows>
            )}
            <Add>
              <Button
                data-testid={`${testId}-add`}
                icon={<PlusIcon size={16} />}
                onClick={() => add(newRow)}
                variant="transparent"
              >
                {addLabel}
              </Button>
            </Add>
          </>
        )}
      </AntdForm.List>
    </List>
  )
}

const { spacing } = cssVars

const Add = styled.div`
  display: flex;
`

const Field = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: ${spacing[50]};
  min-width: 224px;

  .ant-select {
    width: 100%;
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[50]};
`

const Row = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[50]} ${spacing[200]};
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[200]};
`

export default StepRowList
